import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen, Settings, ChevronDown, ChevronRight, CheckCircle2, Circle,
  Clock, HelpCircle, Trophy
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const iconMap = { BookOpen, Settings };

function TopicItem({ topic, moduleId, activeSection, isCompleted, onSelect, isExpanded, onToggle }) {
  const hasSubtopics = topic.subtopics && topic.subtopics.length > 0;
  const hasQuiz = !!topic.quiz;

  const completedCount = topic.subtopics
    ? topic.subtopics.filter(s => isCompleted(s.id)).length + (hasQuiz && isCompleted(topic.quiz.id) ? 1 : 0)
    : 0;
  const totalCount = (topic.subtopics?.length || 0) + (hasQuiz ? 1 : 0);
  const allDone = totalCount > 0 && completedCount === totalCount;

  const isActive = topic.subtopics?.some(s => s.id === activeSection) ||
    (topic.quiz && topic.quiz.id === activeSection);

  return (
    <div className="mb-0.5">
      <button
        data-testid={`topic-${topic.id}`}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md transition-all duration-200",
          "hover:bg-zinc-100 dark:hover:bg-zinc-800",
          isActive && "bg-zinc-50 dark:bg-zinc-900/50",
          allDone && "text-emerald-600 dark:text-emerald-400"
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (hasSubtopics || hasQuiz) {
            onToggle(`${moduleId}-${topic.id}`);
          }
        }}
      >
        {allDone ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
        ) : (
          <Circle className="h-3.5 w-3.5 shrink-0 text-zinc-300 dark:text-zinc-600" />
        )}
        <span className="flex-1 truncate font-medium">{topic.title}</span>
        {totalCount > 0 && (
          <span className="text-[10px] text-zinc-400 dark:text-zinc-500 shrink-0">
            {completedCount}/{totalCount}
          </span>
        )}
        {(hasSubtopics || hasQuiz) && (
          isExpanded
            ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
            : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
        )}
      </button>

      {isExpanded && (
        <div className="ml-4 pl-3 border-l border-zinc-200 dark:border-zinc-700">
          {topic.subtopics?.map(sub => (
            <button
              key={sub.id}
              data-testid={`subtopic-${sub.id}`}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm rounded-md transition-all duration-200",
                "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                activeSection === sub.id && "bg-blue-50 dark:bg-blue-950/30 font-medium border-l-2 border-blue-500 -ml-[1px] text-blue-700 dark:text-blue-300",
                activeSection !== sub.id && isCompleted(sub.id) && "text-zinc-500 dark:text-zinc-400"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(sub.id);
              }}
            >
              {isCompleted(sub.id) ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              ) : (
                <Circle className="h-3 w-3 shrink-0 text-zinc-300 dark:text-zinc-600" />
              )}
              <span className="flex-1 truncate">{sub.title}</span>
              <span className="text-[10px] text-zinc-400 dark:text-zinc-500 flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {sub.readingTime}m
              </span>
            </button>
          ))}
          {hasQuiz && (
            <button
              data-testid={`quiz-${topic.quiz.id}`}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm rounded-md transition-all duration-200",
                "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                activeSection === topic.quiz.id && "bg-amber-50 dark:bg-amber-950/30 font-medium border-l-2 border-amber-500 -ml-[1px]",
                isCompleted(topic.quiz.id) && "text-zinc-500 dark:text-zinc-400"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(topic.quiz.id);
              }}
            >
              {isCompleted(topic.quiz.id) ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              ) : (
                <HelpCircle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              )}
              <span className="flex-1 truncate">{topic.quiz.title}</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">Quiz</Badge>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ modules, activeSection, isCompleted, isBookmarked, onSelect, completionPercent, isAllComplete, onCertificate }) {
  const [expandedModules, setExpandedModules] = useState(new Set(['welcome']));
  const [expandedTopics, setExpandedTopics] = useState(new Set());

  // Auto-expand module and topic containing the active section
  useEffect(() => {
    if (!activeSection) return;
    modules.forEach(mod => {
      mod.topics.forEach(topic => {
        const hasActive = topic.subtopics?.some(s => s.id === activeSection) ||
          (topic.quiz && topic.quiz.id === activeSection);
        if (hasActive) {
          setExpandedModules(prev => {
            if (prev.has(mod.id)) return prev;
            const next = new Set(prev);
            next.add(mod.id);
            return next;
          });
          const topicKey = `${mod.id}-${topic.id}`;
          setExpandedTopics(prev => {
            if (prev.has(topicKey)) return prev;
            const next = new Set(prev);
            next.add(topicKey);
            return next;
          });
        }
      });
      // Check module-level quiz
      if (mod.quiz && mod.quiz.id === activeSection) {
        setExpandedModules(prev => {
          if (prev.has(mod.id)) return prev;
          const next = new Set(prev);
          next.add(mod.id);
          return next;
        });
      }
    });
  }, [activeSection, modules]);

  const toggleModule = useCallback((modId) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(modId)) next.delete(modId);
      else next.add(modId);
      return next;
    });
  }, []);

  const toggleTopic = useCallback((topicKey) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topicKey)) next.delete(topicKey);
      else next.add(topicKey);
      return next;
    });
  }, []);

  return (
    <aside
      data-testid="course-sidebar"
      className="h-full flex flex-col bg-white dark:bg-zinc-950 border-r border-zinc-200 dark:border-zinc-800"
    >
      <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
        <h2 className="font-heading text-sm font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
          Course Outline
        </h2>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-zinc-500">{completionPercent}%</span>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2">
          {modules.map(mod => {
            const ModIcon = iconMap[mod.icon] || BookOpen;
            const isModExpanded = expandedModules.has(mod.id);
            return (
              <div key={mod.id} className="mb-2">
                <button
                  data-testid={`module-${mod.id}`}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2.5 text-left rounded-lg transition-all duration-200",
                    "hover:bg-zinc-50 dark:hover:bg-zinc-900",
                    isModExpanded && "bg-zinc-50 dark:bg-zinc-900"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleModule(mod.id);
                  }}
                >
                  <ModIcon className="h-4 w-4 shrink-0 text-blue-500" />
                  <span className="flex-1 text-sm font-semibold text-zinc-800 dark:text-zinc-200 truncate">
                    {mod.title}
                  </span>
                  {isModExpanded
                    ? <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400" />
                    : <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400" />}
                </button>

                {isModExpanded && (
                  <div className="ml-2">
                    {mod.topics.map(topic => (
                      <TopicItem
                        key={topic.id}
                        topic={topic}
                        moduleId={mod.id}
                        activeSection={activeSection}
                        isCompleted={isCompleted}
                        onSelect={onSelect}
                        isExpanded={expandedTopics.has(`${mod.id}-${topic.id}`)}
                        onToggle={toggleTopic}
                      />
                    ))}
                    {mod.quiz && (
                      <button
                        data-testid={`quiz-${mod.quiz.id}`}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md transition-all duration-200",
                          "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                          activeSection === mod.quiz.id && "bg-amber-50 dark:bg-amber-950/30 font-medium",
                          isCompleted(mod.quiz.id) && "text-zinc-500"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(mod.quiz.id);
                        }}
                      >
                        {isCompleted(mod.quiz.id) ? (
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                        ) : (
                          <HelpCircle className="h-4 w-4 shrink-0 text-amber-500" />
                        )}
                        <span className="flex-1 truncate">{mod.quiz.title}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">Quiz</Badge>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

    </aside>
  );
}
